import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import ContactCTAs from "@/components/ContactCTAs";
import { api } from "@/lib/api";
import { Phone, ShieldCheck, Clock, Star } from "@phosphor-icons/react";

const HERO = "https://images.pexels.com/photos/15774577/pexels-photo-15774577.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ARUBA_IMG = "https://images.pexels.com/photos/12813576/pexels-photo-12813576.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AVIATION = "https://images.unsplash.com/photo-1635672033263-a19f27eaefa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwYXZpYXRpb258ZW58MHx8fHwxNzg0NDk3NTM0fDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [tracking, setTracking] = useState(null);
  useEffect(() => {
    api.get("/tracking/aruba").then((r) => setTracking(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-cream min-h-screen">
      <Nav />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Chauffeur opening door" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-deep/70" />
          <div className="absolute inset-0 grain opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-24 sm:pt-24 sm:pb-32">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="text-[11px] uppercase tracking-[0.35em] text-gold-bright mb-6 animate-fade-up" data-testid="hero-eyebrow">
                — Caribbean Private Transfers
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-offwhite leading-[0.95] animate-fade-up" data-testid="hero-headline">
                Arrive as if you were <em className="text-gold-bright not-italic">expected.</em>
              </h1>
              <p className="mt-6 text-offwhite/80 text-base sm:text-lg leading-relaxed max-w-xl animate-fade-up" data-testid="hero-sub">
                One number, one driver, dispatched live. Call, WhatsApp, or reserve ahead — whichever fits your trip.
              </p>
              <ContactCTAs tracking={tracking} className="mt-8 animate-fade-up" />
              <div className="mt-8">
                <Link
                  to="/how-it-works"
                  data-testid="hero-cta-how"
                  className="text-xs uppercase tracking-[0.28em] text-offwhite/70 hover:text-gold-bright border-b border-offwhite/25 pb-1"
                >
                  How it works
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 animate-fade-up" data-testid="home-lead-form-slot">
              <LeadForm islandSlug="aruba" variant="dark" />
            </div>
          </div>
        </div>
      </section>

      {/* Bento trust grid */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-6 gap-6">
          <div className="md:col-span-4 border border-cream2 p-10 bg-cream">
            <div className="text-[10px] uppercase tracking-[0.28em] text-teal mb-4">Our Promise</div>
            <h2 className="font-display text-3xl sm:text-4xl text-navy leading-tight">
              The quiet standard for <em className="text-gold not-italic">private airport transfers.</em>
            </h2>
            <p className="mt-6 text-navy/70 leading-relaxed max-w-2xl">
              LocalRida partners exclusively with licensed transportation businesses on each island —
              never freelance drivers. Every ride is dispatched to a vetted operator, and we cover the full
              island: hotels, resorts, and vacation rentals; airport, cruise port, and San Nicolas alike.
              Every call is recorded for service integrity, every trip met with the same discretion you expect at check-in.
            </p>
          </div>
          <div className="md:col-span-2 border border-cream2 p-8 bg-navy text-cream flex flex-col justify-between">
            <ShieldCheck size={36} weight="thin" className="text-gold-bright" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold-bright mb-3">Vetted</div>
              <p className="text-cream/85 text-sm leading-relaxed">Licensed operators only. Informed consent required before any lead is routed.</p>
            </div>
          </div>
          <div className="md:col-span-2 border border-cream2 p-8">
            <Clock size={32} weight="thin" className="text-teal" />
            <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-navy/60 mb-2">Response</div>
            <div className="font-display text-4xl text-navy">Minutes</div>
            <p className="text-navy/60 text-sm mt-2">Average answer time on the tracking line.</p>
          </div>
          <div className="md:col-span-2 border border-cream2 p-8">
            <Phone size={32} weight="thin" className="text-teal" />
            <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-navy/60 mb-2">One Number</div>
            <div className="font-display text-4xl text-navy">Per Island</div>
            <p className="text-navy/60 text-sm mt-2">The same line, every time you land.</p>
          </div>
          <div className="md:col-span-2 border border-cream2 p-8">
            <Star size={32} weight="thin" className="text-teal" />
            <div className="mt-6 text-[10px] uppercase tracking-[0.28em] text-navy/60 mb-2">Class</div>
            <div className="font-display text-4xl text-navy">SUV · Van</div>
            <p className="text-navy/60 text-sm mt-2">Premium cabins with room for luggage and family.</p>
          </div>
        </div>
      </section>

      {/* Aruba spotlight */}
      <section className="bg-navy-deep text-offwhite">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.35em] text-gold-bright mb-6">Now Live</div>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.95]">Aruba.</h2>
            <p className="mt-6 text-offwhite/75 leading-relaxed max-w-lg">
              From AUA to the high-rise strip, from Oranjestad to Eagle Beach — one call, one licensed operator, on-time and on record.
            </p>
            <Link
              to="/aruba"
              data-testid="home-aruba-cta"
              className="mt-10 inline-flex items-center gap-3 border border-gold-bright text-gold-bright px-8 py-4 text-xs uppercase tracking-[0.28em] hover:bg-gold-bright hover:text-navy-deep transition-colors"
            >
              Enter Aruba
              <span className="w-6 h-px bg-current" />
            </Link>
            {tracking && (
              <div className="mt-10 border-t border-navy-soft pt-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-offwhite/50 mb-2">Direct line</div>
                <a
                  href={`tel:${tracking.number_e164}`}
                  className="font-display text-3xl text-offwhite hover:text-gold-bright transition-colors"
                  data-testid="home-phone-link"
                >
                  {tracking.display}
                </a>
              </div>
            )}
          </div>
          <div className="relative aspect-[4/5]">
            <img src={ARUBA_IMG} alt="Aruba coastline" className="w-full h-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/30" />
          </div>
        </div>
      </section>

      {/* Aviation feel band */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-5 gap-10 items-center">
          <div className="md:col-span-2 aspect-[4/3]">
            <img src={AVIATION} alt="Private aviation" className="w-full h-full object-cover" />
          </div>
          <div className="md:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.28em] text-teal mb-4">Standard</div>
            <h3 className="font-display text-4xl text-navy leading-tight">
              Held to a private-aviation standard.
            </h3>
            <p className="mt-5 text-navy/70 leading-relaxed">
              We built LocalRida for the traveler who values quiet consistency: a car that meets you at the curb,
              a driver who greets you by name, and a routing system that never sends your call to voicemail without a follow-up.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
